import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, TrendingUp, Users, Coins } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Leaderboard } from '@/components/leaderboard';
import { useChallenge } from '@/hooks/useChallenge';
import { useAuth } from '@/hooks/useAuth';
import { formatNumber, formatDate } from '@/utils/formatters';
import { getChallengeJoinability } from '@/utils/challengeHelpers';

export function Challenge() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedChallenge, fetchChallenge, joinChallenge, leaveChallenge, isLoading } =
    useChallenge();

  useEffect(() => {
    if (id) {
      fetchChallenge(id);
    }
  }, [id, fetchChallenge]);

  const handleJoin = async () => {
    if (!id || !selectedChallenge) return;

    // Use centralized joinability check
    const currentJoinability = getChallengeJoinability(
      selectedChallenge,
      !!selectedChallenge.currentUserParticipant
    );

    if (!currentJoinability.canJoin) {
      console.error('Cannot join:', currentJoinability.message);
      return;
    }

    try {
      await joinChallenge(id, selectedChallenge.stakeAmount);
    } catch (error) {
      console.error('Failed to join challenge:', error);
    }
  };

  const handleLeave = async () => {
    if (id && confirm('Are you sure you want to leave this challenge?')) {
      try {
        await leaveChallenge(id);
        navigate('/challenges');
      } catch (error) {
        console.error('Failed to leave challenge:', error);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-background-dark flex items-center justify-center pt-[env(safe-area-inset-top)]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!selectedChallenge) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-background-dark flex items-center justify-center pt-[env(safe-area-inset-top)]">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Challenge not found</h2>
          <Button onClick={() => navigate('/challenges')}>Back to Challenges</Button>
        </div>
      </div>
    );
  }

  const isParticipant = !!selectedChallenge.currentUserParticipant;

  // Use centralized joinability check - single source of truth
  const joinability = getChallengeJoinability(
    selectedChallenge,
    isParticipant
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-background-dark pb-20 md:pb-8">
      {/* Sticky header with safe-area */}
      <div className="sticky top-0 z-10 bg-gray-50 dark:bg-background-dark pt-[env(safe-area-inset-top)]">
        <div className="container mx-auto px-4 pt-4">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
      </div>
      <div className="container mx-auto px-4">

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Challenge Details */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-2xl mb-2">{selectedChallenge.name}</CardTitle>
                    {selectedChallenge.description && (
                      <p className="text-gray-600 dark:text-gray-400">{selectedChallenge.description}</p>
                    )}
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      selectedChallenge.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : selectedChallenge.status === 'upcoming'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {selectedChallenge.status}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Daily Goal</p>
                      <p className="font-semibold">{formatNumber(selectedChallenge.dailyGoal)} km</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Duration</p>
                      <p className="font-semibold">{selectedChallenge.durationDays} days</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Users className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Participants</p>
                      <p className="font-semibold">{selectedChallenge.participantCount || 0}</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                      <Coins className="w-5 h-5 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Stake</p>
                      <p className="font-semibold">${selectedChallenge.stakeAmount}</p>
                    </div>
                  </div>
                </div>

                <div className="border-t dark:border-gray-700 pt-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-500 dark:text-gray-400">Start Date</span>
                    <span className="font-medium dark:text-white">{formatDate(selectedChallenge.startDate)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">End Date</span>
                    <span className="font-medium dark:text-white">{formatDate(selectedChallenge.endDate)}</span>
                  </div>
                </div>

                {isParticipant ? (
                  <Button variant="destructive" onClick={handleLeave} className="w-full">
                    Leave Challenge
                  </Button>
                ) : joinability.canJoin ? (
                  <Button onClick={handleJoin} className="w-full">
                    {joinability.buttonText}
                  </Button>
                ) : (
                  <Button disabled className="w-full">
                    {joinability.buttonText}
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Leaderboard */}
          <div className="lg:col-span-1">
            <Leaderboard participants={selectedChallenge.participants || []} currentUserId={user?.id} />
          </div>
        </div>
      </div>
    </div>
  );
}
